import { DateTime } from "luxon";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	DeviceCategory,
	VelavuAlert,
	VelavuAPI,
	VelavuDevice,
} from "velavu-js-api";
import { useTranslation } from "react-i18next";
import IconMoreHorizontal from "../../dynamicicons/icon-more-horizontal";
import AlertIcon from "../../elements/alert-icon";
import Divider from "../../elements/divider";
import VelavuIconButton from "../../elements/velavu-icon-button";
import { useVelavuModal } from "../../elements/velavu-modal";
import { VelavuTitledModal } from "../../elements/velavu-titled-modal";
import { buildEventDescription } from "../../helper/language-helper";
import whitelabel from "../../whitelabel";
import DetailModal from "../asset-tag-detail/detail-modal";
import * as styles from "./alert-item.module.scss";
import DetailModalLoading from "../asset-tag-detail/detail-modal-loading";
import { classArr } from "../../helper/style-helper";

interface AlertItemProps {
	alert: VelavuAlert;
	unread: boolean;
	baseTime: DateTime;

	onOpenMenu?: (event: AlertsMenu) => void;
	menuOpen?: boolean;
}

export interface AlertsMenu {
	alert: VelavuAlert;
	anchor: HTMLElement;
	unread: boolean;
}

const AlertItem = React.memo(
	({ alert, baseTime, unread, onOpenMenu, menuOpen }: AlertItemProps) => {
		const { t } = useTranslation();
		const date: string = useMemo(() => {
			const eventDate = DateTime.fromISO(alert.timestamp).set({
				millisecond: 0,
				second: 0,
			});

			//If the event is within the last minute, show "Just now"
			const JUST_NOW_THRESHOLD = 1000 * 60;
			if (
				Math.abs(baseTime.toMillis() - eventDate.toMillis()) <
				JUST_NOW_THRESHOLD
			) {
				return t("alerts.just_now");
			} else {
				return eventDate.toRelative({ base: baseTime })!;
			}
		}, [alert.timestamp, baseTime, t]);

		const { pushModal } = useVelavuModal();

		const deviceID = alert.device.id;
		const handleAlertItemClick = useCallback(() => {
			//Fetch the device and asset information
			pushModal(
				(close) => (
					<AsyncDeviceDetailModal deviceID={deviceID} close={close} />
				),
				{ pinToTop: true },
			);
		}, [pushModal, deviceID]);

		const onMoreButtonClick = useCallback(
			(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
				event.stopPropagation();

				onOpenMenu?.({
					alert: alert,
					anchor: event.currentTarget,
					unread: unread,
				});
			},
			[onOpenMenu],
		);

		return (
			<button
				className={classArr(
					styles.container,
					menuOpen && styles.selected,
					unread && styles.unread,
				)}
				onClick={handleAlertItemClick}
			>
				<div className={styles.row}>
					<span
						className={classArr(styles.title, styles.collapsable)}
					>
						{alert.asset?.name ?? alert.device.id}
					</span>

					<VelavuIconButton
						className={styles.moreButton}
						onClick={onMoreButtonClick}
						focus={menuOpen}
						small
					>
						<IconMoreHorizontal
							color={whitelabel.palette.secondary700}
						/>
					</VelavuIconButton>
				</div>

				<div className={styles.row}>
					<span
						className={classArr(
							styles.subtitle,
							styles.collapsable,
						)}
					>
						<AlertIcon
							type={alert.event_category}
							size={16}
							color="currentColor"
						/>
						<span className={styles.collapsable}>
							{buildEventDescription(alert, t)}
						</span>
					</span>

					<span className={styles.date}>{date}</span>
				</div>
			</button>
		);
	},
);

export default AlertItem;

interface AsyncDeviceDetailModalProps {
	deviceID: string;
	close: () => void;
}

function AsyncDeviceDetailModal(props: AsyncDeviceDetailModalProps) {
	const { t } = useTranslation();

	//Load the device
	const [device, setDevice] = useState<VelavuDevice | undefined>(undefined);
	useEffect(() => {
		VelavuAPI.devices.getSpecificDevice(props.deviceID).then((device) => {
			setDevice(device);
		});
	}, [props.deviceID, setDevice]);

	if (device !== undefined) {
		const asset = device.asset;

		return (
			<VelavuTitledModal
				title={
					asset
						? t("alerts.asset_details")
						: t("alerts.device_details")
				}
				onClose={props.close}
			>
				<DetailModal
					asset={asset}
					device={device}
					deviceOnly={device?.category === DeviceCategory.Anchor}
					defaultTab={asset ? "asset" : "device"}
					onClose={props.close}
				/>
			</VelavuTitledModal>
		);
	} else {
		return (
			<VelavuTitledModal title="" onClose={props.close}>
				<DetailModalLoading />
			</VelavuTitledModal>
		);
	}
}
